/**
 * PipelineTimeline - Interactive stage timeline for HI-SI pipeline execution
 * Replaces basic progress bar with comprehensive stage visualization
 */

import React, { useState, useEffect } from 'react';
import { StageType, ExecutionStatus, StageMetrics } from '@/types/execution';
import { cn } from '@/utils/cn';
import { useDashboardServerStore } from '@/stores/dashboard-server';
import StageNode from './StageNode';
import StageConnector from './StageConnector';
import StageDetailsPanel from './StageDetailsPanel';
import { StageIteration } from './IterationTracker';
import { StageQualityMetrics } from './QualityMetrics';

interface PipelineTimelineProps {
  execution: ExecutionStatus;
  onStageClick?: (stageId: StageType) => void;
  className?: string;
}

// Pipeline stages - actual processing stages only (agent is orchestrator, not a stage)
const PIPELINE_STAGES: Array<{ id: StageType; label: string; description: string }> = [
  { id: 'script', label: 'Script', description: 'Generate story and messaging' },
  { id: 'vision', label: 'Vision', description: 'Set look, tone, and style' },
  { id: 'storyboard', label: 'Storyboard', description: 'Plan the visual narrative' },
  { id: 'shots', label: 'Shots', description: 'Define shots and camera moves' },
  { id: 'continuity', label: 'Continuity', description: 'Keep visuals consistent' },
  { id: 'frames', label: 'Frame Generation', description: 'Create key visual frames' },
  { id: 'videos', label: 'Video Generation', description: 'Assemble motion from frames' },
  { id: 'assemble', label: 'Assembly', description: 'Combine and sequence outputs' },
  { id: 'music', label: 'Music', description: 'Compose and fit soundtrack' },
  { id: 'publish', label: 'Publishing', description: 'Export and deliver' },
];

const PipelineTimeline: React.FC<PipelineTimelineProps> = ({
  execution,
  onStageClick,
  className
}) => {
  const [activeStage, setActiveStage] = useState<StageType | null>(null);
  const [showDetailsPanel, setShowDetailsPanel] = useState(false);
  const [stageIterations, setStageIterations] = useState<Record<string, StageIteration[]>>({});
  const [qualityMetrics, setQualityMetrics] = useState<Record<string, StageQualityMetrics>>({});
  const [loading, setLoading] = useState(true);
  
  // Get real stage metrics from the dashboard store
  const { getStageMetrics } = useDashboardServerStore();
  const stageMetrics = execution?.id ? getStageMetrics(execution.id) : [];

  // Load real iteration and quality data
  useEffect(() => {
    const loadRealData = async () => {
      try {
        setLoading(true);

        // Fetch iterations and quality metrics in parallel
        const [iterationsResponse, qualityResponse] = await Promise.all([
          fetch(`/api/iterations/${execution.id}`, { credentials: 'include' }),
          fetch(`/api/iterations/${execution.id}/quality-metrics`, { credentials: 'include' })
        ]);

        if (iterationsResponse.ok && qualityResponse.ok) {
          const iterationsData = await iterationsResponse.json();
          const qualityData = await qualityResponse.json();

          if (iterationsData.success && qualityData.success) {
            // Group iterations by stage
            const iterationsByStage: Record<string, StageIteration[]> = {};
            iterationsData.iterations.forEach((iteration: any) => {
              if (!iterationsByStage[iteration.stage]) {
                iterationsByStage[iteration.stage] = [];
              }
              iterationsByStage[iteration.stage].push({
                iteration_number: iteration.iteration_number,
                decision: iteration.decision,
                scores: iteration.scores || {},
                feedback: iteration.feedback || '',
                improved_elements: iteration.improved_elements || [],
                preserved_elements: iteration.preserved_elements || [],
                persona_name: iteration.persona_name || 'The Generator',
                is_best_attempt: iteration.is_best_attempt || false
              });
            });

            setStageIterations(iterationsByStage);
            setQualityMetrics(qualityData.quality_metrics || {});
          }
        }
      } catch (error) {
        console.error('Failed to load real pipeline data:', error);
        // Fall back to empty state, not mocks
        setStageIterations({});
        setQualityMetrics({});
      } finally {
        setLoading(false);
      }
    };

    loadRealData();

    // Refresh data every 5 seconds for active executions
    const refreshInterval = execution.status === 'running' 
      ? setInterval(loadRealData, 5000)
      : null;

    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, [execution.id, execution.status]);

  // Get real iteration data for a stage
  const getStageIterations = (stageId: StageType): StageIteration[] => {
    return stageIterations[stageId] || [];
  };

  // Get real quality metrics for a stage
  const getStageQualityMetrics = (stageId: StageType): StageQualityMetrics | undefined => {
    return qualityMetrics[stageId];
  };

  const getStageMetricsByStage = (stageId: StageType): StageMetrics => {
    // Find the specific stage metrics from the store
    const metrics = stageMetrics.find(m => m.stage === stageId);
    
    if (metrics) {
      return metrics;
    }

    // Fallback to computed metrics if not found in store
    const currentStage = execution?.current_stage;
    const isCompleted = currentStage ? (PIPELINE_STAGES.findIndex(s => s.id === stageId) < 
                       PIPELINE_STAGES.findIndex(s => s.id === currentStage)) : false;
    const isCurrent = currentStage ? stageId === currentStage : false;

    return {
      stage: stageId,
      status: isCompleted ? 'completed' : isCurrent ? 'running' : 'pending',
      progress: isCompleted ? 1.0 : isCurrent ? (execution?.progress?.current_stage || 0) : 0.0,
      cost_incurred: 0,
      errors: 0,
      quality_score: undefined,
      duration: undefined,
    };
  };

  const handleStageClick = (stageId: StageType) => {
    setActiveStage(stageId);
    setShowDetailsPanel(true);
    onStageClick?.(stageId);
  };

  const handleCloseDetails = () => {
    setShowDetailsPanel(false);
    setActiveStage(null);
  };

  return (
    <div className={cn('space-y-6', className)}>
      {/* Timeline Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Pipeline Progress</h3>
          <p className="text-sm text-gray-600">
            {Math.round((execution?.progress?.overall || 0) * 100)}% complete • 
            {(execution?.progress?.stages_completed || 0)} of {PIPELINE_STAGES.length} stages
            {loading && <span className="ml-2 text-blue-600">• Loading real data...</span>}
          </p>
        </div>
        <div className="text-right">
          <div className="text-sm font-medium text-gray-900">
            ${(execution?.cost_incurred || 0).toFixed(2)} / ${(execution?.cost_projected || 0).toFixed(2)}
          </div>
          <div className="text-xs text-gray-500">Cost incurred / projected</div>
        </div>
      </div>

      {/* Interactive Timeline */}
      <div className="relative">
        {/* Desktop Timeline - Horizontal */}
        <div className="hidden md:block">
          <div className="flex items-center justify-between">
            {PIPELINE_STAGES.map((stage, index) => {
              const metrics = getStageMetricsByStage(stage.id);
              const isLast = index === PIPELINE_STAGES.length - 1;
              
              const iterations = getStageIterations(stage.id);
              const qualityMetrics = getStageQualityMetrics(stage.id);
              
              return (
                <div key={stage.id} className="flex items-center flex-1">
                  <StageNode
                    stage={stage}
                    metrics={metrics}
                    isActive={activeStage === stage.id}
                    onClick={() => handleStageClick(stage.id)}
                    iterations={iterations}
                    currentIteration={iterations.length}
                    maxIterations={stage.id === 'shots' || stage.id === 'frames' ? 5 : 3}
                    qualityMetrics={qualityMetrics}
                    showIterations={true}
                    showPersona={metrics.status === 'running'}
                    showQuality={metrics.status !== 'pending'}
                    className="flex-shrink-0"
                  />
                  {!isLast && (
                    <StageConnector
                      fromStage={stage.id}
                      toStage={PIPELINE_STAGES[index + 1].id}
                      status={metrics.status === 'completed' ? 'completed' : 'pending'}
                      className="flex-1 mx-2"
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Mobile/Tablet Timeline - Vertical */}
        <div className="md:hidden space-y-4">
          {PIPELINE_STAGES.map((stage, index) => {
            const metrics = getStageMetricsByStage(stage.id);
            const isLast = index === PIPELINE_STAGES.length - 1;
            const iterations = getStageIterations(stage.id);
            const qualityMetrics = getStageQualityMetrics(stage.id);
            
            return (
              <div key={stage.id} className="space-y-2">
                <StageNode
                  stage={stage}
                  metrics={metrics}
                  isActive={activeStage === stage.id}
                  onClick={() => handleStageClick(stage.id)}
                  layout="vertical"
                  className="w-full"
                  iterations={iterations}
                  currentIteration={iterations.length}
                  maxIterations={stage.id === 'shots' || stage.id === 'frames' ? 5 : 3}
                  qualityMetrics={qualityMetrics}
                  showIterations={true}
                  showPersona={metrics.status === 'running'}
                  showQuality={metrics.status !== 'pending'}
                />
                {!isLast && (
                  <StageConnector
                    fromStage={stage.id}
                    toStage={PIPELINE_STAGES[index + 1].id}
                    status={metrics.status === 'completed' ? 'completed' : 'pending'}
                    orientation="vertical"
                    className="ml-8 h-6"
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Stage Details Panel */}
      {showDetailsPanel && activeStage && (
        <StageDetailsPanel
          stage={PIPELINE_STAGES.find(s => s.id === activeStage)!}
          metrics={getStageMetricsByStage(activeStage)}
          execution={execution}
          onClose={handleCloseDetails}
        />
      )}
    </div>
  );
};

export default PipelineTimeline;